import { Component, OnDestroy, OnInit } from "@angular/core";
import { NgForm } from '@angular/forms';
import { AccountClient, CustomerClient, WebCompany, WebCustomer } from "../../../models";
import { MatDialog } from "@angular/material/dialog";
import { PasswordUpdate } from "../../../models/password-update";
import { CompanyService, CustomerService } from "../../../services";
import { Subscription } from "rxjs";
import { ConfirmSetPwDialogComponent } from "../../set-password/confirm-set-pw-dialog/confirm-set-pw-dialog.component";
import { ErrorDialogComponent } from "../../../components/error-dialog/error-dialog.component";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { filter } from "rxjs/operators";


@Component({
    selector: "app-update-password-dialog",
    templateUrl: "./update-password-dialog.component.html",
    styleUrls: ["./update-password-dialog.component.scss"],
})
export class UpdatePasswordDialogComponent implements OnInit, OnDestroy
{

    public passwordRequirements: any;
    public customerSubscription: Subscription;
    public customer: WebCustomer;
    // note to self: per angular pluralsite tutorial, never edit original data, work with a copy.
    // this is to prevent a data corruption issue that can happen in angular if user starts entering information and
    // then exits out without saving, hits back button, etc.

    public companySubscription: Subscription;
    public company: WebCompany;
    public originalPasswordUpdate: PasswordUpdate = {};
    public passwordUpdate: PasswordUpdate = { ...this.originalPasswordUpdate };
    public inputText: string = "";
    public currentPassword: string;
    public validRequirements = new Set<string>();
    alertMessage: string;
    isNewPasswordValid: boolean = false;
    hideCurrentPassword: boolean;
    hideNewPassword: boolean;
    hideConfirmNewPassword: boolean;
    showSpinner: boolean;

    constructor(
        private customerService: CustomerService,
        private companyService: CompanyService,
        public customerClient: CustomerClient,
        public dialog: MatDialog,
        private accountClient: AccountClient,

    ) { }

    ngOnInit(): void
    {
        this.customerSubscription = this.customerService.customer
            .pipe(filter(customer => !!customer))
            .subscribe(customer =>
            {
                this.customer = customer;
            });

        this.companySubscription = this.companyService.company.subscribe(company => 
        {
            this.company = company;
        });
        this.setPasswordInfo();
    }

    setPasswordInfo()
    {
        this.showSpinner = false;
        if (this.company?.Version?.startsWith("10.02"))
        {
            this.accountClient.getPasswordRequirements().subscribe(p => 
            {
                if (p)
                {
                    this.passwordRequirements = p;
                }
                else
                {
                    this.alertMessage = ("There was a problem loading the password requirements. Please try again later.")
                    this.alertDialog(this.alertMessage)
                }
            });
        }
        else
        {
            this.accountClient.getPasswordRequirementsV2().subscribe(p =>
            {
                if (p)
                {
                    this.passwordRequirements = p;
                }
                else
                {
                    this.alertMessage = ("There was a problem loading the password requirements. Please try again later.")
                    this.alertDialog(this.alertMessage)
                }
            });
        }
    }

    validateInput(password: string): void
    {
        this.validRequirements.clear();
        if (!password)
        {
            return;
        }
        for (const requirement in this.passwordRequirements)
        {
            if (this.passwordRequirements.hasOwnProperty(requirement))
            {
                const regex = new RegExp(this.passwordRequirements[requirement]);
                const match = regex.test(password);
                if (match)
                {
                    this.validRequirements.add(requirement);
                }
            }
        }
        const requirementSize = Object.keys(this.passwordRequirements).length;
        this.isNewPasswordValid = this.validRequirements.size === requirementSize;
        if (this.isNewPasswordValid)
        {
            this.passwordUpdate.NewPassword = password;
        }
        else
        {
            return;
        }
    }

    submitPasswordUpdate(form: NgForm)
    {
        if (!form.valid)
        {
            if (!this.passwordUpdate.OldPassword || !this.passwordUpdate.NewPassword || !this.passwordUpdate.ConfirmPassword)
            {
                this.alertMessage = "Please ensure all fields are filled in correctly."
                this.alertDialog(this.alertMessage);
            }
        }
        else if (this.passwordUpdate.NewPassword != this.passwordUpdate.ConfirmPassword && this.isNewPasswordValid)
        {
            this.alertMessage = "Your confirmation password does not match your new password."
            this.alertDialog(this.alertMessage)
        }
        else if (!this.isNewPasswordValid)
        {
            this.alertMessage = "Your password does not meet the requirements shown below."
            this.alertDialog(this.alertMessage);
        }
        else
        {
            this.showSpinner = true;
            this.customerService.updatePassword(this.customer.CustId, this.customer.AcctNo, this.passwordUpdate.OldPassword, this.passwordUpdate.NewPassword)
                .subscribe(r =>
                {
                    if (r)
                    {
                        this.confirmUpdate();
                    }
                    else
                    {
                        this.errorDialog("Please try again.")
                    }
                }, error =>
                {
                    this.showSpinner = false;
                    this.errorDialog(error);
                });
        }
    }

    alertDialog(message)
    {
        this.showSpinner = false;
        this.dialog.open(AlertDialogComponent, {
            panelClass: "form-field-less-than-180",
            width: "500px",
            height: "200px",
            data: {
                title: "Invalid form",
                message: message, buttonText: "Close"
            }
        });
    }

    errorDialog(error)
    {
        this.showSpinner = false;
        this.dialog.open(ErrorDialogComponent, {
            panelClass: "form-field-less-than-180",
            maxWidth: "600px",
            maxHeight: "500px",
            disableClose: true,
            data: "There was an error updating your password. " + error
        });
    }

    confirmUpdate()
    {
        this.showSpinner = false;
        const dialogRef = this.dialog.open(ConfirmSetPwDialogComponent, {
            panelClass: "form-field-less-than-180",
            maxWidth: "600px",
            maxHeight: "500px",
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(_ =>
        {
            this.dialog.closeAll();
            this.customerService.populateData();
        });
    }
    ngOnDestroy()
    {
        this.customerSubscription.unsubscribe();
        this.companySubscription.unsubscribe();
    }

}
